import { ENDPOINTS } from "@/api/application-api";
import { jsonAuthHeaders } from "@/utils/headers";
import { useQuery } from "@tanstack/react-query";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { useParams } from "react-router-dom";
import { Response } from "../types";
import { Model } from "./types";
import { useState } from "react";

export const useModels = ({
  userToken,
  providerId,
  enabled = true,
  q,
  allowEmptySearch = false,
}: {
  userToken: string;
  providerId?: number;
  enabled?: boolean;
  q?: string;
  allowEmptySearch?: boolean;
}) => {
  const { workspaceId } = useParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(250);

  const { data, isLoading, isRefetching, isFetching } = useQuery<
    Response<Model[], "model_configs", string>
  >({
    queryKey: ["models", workspaceId, providerId, page, pageSize, q],
    queryFn: async () => {
      const url = new URL(ENDPOINTS.models(workspaceId || ""));
      url.searchParams.set("page", page.toString());
      url.searchParams.set("per_page", pageSize.toString());
      if (providerId) {
        url.searchParams.set("provider_id", providerId?.toString() || "");
      }

      if (allowEmptySearch || q) {
        url.searchParams.set("q", q || "");
      }

      const response = await fetch(`${url}`, {
        headers: jsonAuthHeaders(userToken),
        method: "GET",
      });
      return handleResponse(response);
    },
    enabled: (!!providerId || !!allowEmptySearch) && enabled,
    staleTime: 1000 * 60 * 60 * 24,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  return {
    data,
    isLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
    isRefetching,
    isFetching,
  };
};
